import type { ApiInvoicePositionSnapshot } from '@Model';
import { Badge, type Column } from '@synqo/frontend-component-library';

/**
 * What the "Auftrag" cell needs to become a link. The order itself has no page of its own here, so
 * the cell leads to the requisition behind it — the same target as the frame's header button.
 * @ai-generated 2026-08-28T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
 */
export interface InvoiceSnapshotOrderLink {
    /** Requisition id per order snapshot id, for the orders this invoice billed. */
    requisitionByOrderSnapshot: ReadonlyMap<string, string>;

    /** Opens the requisition behind one of those orders. */
    onOpenRequisition: (requisitionId: string) => void;
}

/**
 * Columns of an invoice snapshot's position table. Without an {@link InvoiceSnapshotOrderLink} the
 * order stays plain text, which is what the table shows while the requisitions are still loading.
 */
// @ai-generated 2026-08-28T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
export function invoiceSnapshotPositionColumns(
    orderLink?: InvoiceSnapshotOrderLink,
): Column<ApiInvoicePositionSnapshot>[] {
    return [
        {
            name: 'number',
            label: 'Nr.',
            size: 75,
            disableColumnFilter: true,
        },
        { name: 'label', label: 'Leistung' },
        // The order this position was billed from. Its display string is the order number, which is what a
        // portal user recognises - the 1:1 relation itself is order_position, which carries no useful label.
        {
            name: 'order.display_string',
            label: 'Auftrag',
            renderCell: orderLink && (({ rowData }) => renderOrderCell(rowData, orderLink)),
        },
        { name: 'date_of_performance', label: 'Leistungsdatum', type: 'date', filterVariant: 'day-range' },
        { name: 'place_of_performance.display_string', label: 'Leistungsort' },
        { name: 'quantity', label: 'Menge', type: 'decimal', decimalPlaces: 3, size: 150 },
        { name: 'price_per_unit', label: 'Einzelpreis', type: 'currency', size: 150 },
        { name: 'vat_rate', label: 'MwSt.', type: 'decimal', decimalPlaces: 2, size: 100 },
        { name: 'total_net_price', label: 'Netto in €', type: 'currency', size: 150 },
        { name: 'total_gross_price', label: 'Brutto in €', type: 'currency', size: 150 },
    ];
}

/**
 * The order number, clickable when its requisition is known. A position billed outside of an order,
 * or one whose order snapshot no longer resolves, keeps the plain number rather than a dead badge.
 * @ai-generated 2026-08-28T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
 */
function renderOrderCell(position: ApiInvoicePositionSnapshot, orderLink: InvoiceSnapshotOrderLink) {
    const orderNumber = position.order.display_string;
    const requisitionId = orderLink.requisitionByOrderSnapshot.get(position.order.foreign_key ?? '');

    if (requisitionId === undefined) {
        return orderNumber;
    }

    return (
        <Badge
            variant="brand"
            label={`Zur Bestellung von Auftrag ${orderNumber}`}
            onClick={() => orderLink.onOpenRequisition(requisitionId)}
        >
            {orderNumber}
        </Badge>
    );
}
